// ===========================================
// PREMIUM MAIN LAYOUT - VP-Level Design
// Matching Tripnovac Platform Hub Quality
// Combines Sidebar & Header with Content Area
// ===========================================

import React from 'react';
import { Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import PremiumSidebar from './PremiumSidebar';
import PremiumHeader from './PremiumHeader';
import { useUIStore } from '../../store';

const PremiumMainLayout: React.FC = () => {
  const { sidebarCollapsed } = useUIStore();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Background Pattern */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {/* Subtle Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(139, 92, 246, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(139, 92, 246, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
        
        {/* Gradient Orbs */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-violet-200/30 via-purple-200/20 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-blue-200/20 via-cyan-200/10 to-transparent rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />
        <div className="absolute top-1/2 left-1/2 w-[500px] h-[500px] bg-gradient-to-br from-fuchsia-200/10 to-transparent rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      </div>

      {/* Sidebar */}
      <PremiumSidebar />

      {/* Header */}
      <PremiumHeader />

      {/* Main Content Area */}
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className={`
          relative z-10 pt-16 min-h-screen flex flex-col
          transition-all duration-300 ease-out
          ${sidebarCollapsed ? 'ml-20' : 'ml-72'}
        `}
      >
        {/* Content Container */}
        <div className="p-6 lg:p-8 flex-1">
          {/* Note: no transform animation here — it breaks fixed modal positioning */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </div>

        {/* Footer - Compact VP-Level Design */}
        <footer className="mt-auto bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
            {/* Compact Layout - All in rows */}
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              {/* Brand */}
              <div className="flex items-center gap-3">
                <img src="/assets/tripnovac-logo.jpeg" alt="Tripnovac" className="w-8 h-8 rounded-lg object-cover" />
                <div>
                  <h3 className="text-base font-bold text-white">Tripnovac</h3>
                  <p className="text-violet-400 text-xs">Sales & Marketing</p>
                </div>
                <a href="https://www.tripnovac.com" target="_blank" rel="noopener noreferrer" className="ml-4 text-xs text-gray-400 hover:text-white flex items-center gap-1">
                  tripnovac.com <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                </a>
                <div className="ml-4 flex items-center gap-2 px-3 py-1 bg-gray-800/50 rounded-full">
                  <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span></span>
                  <span className="text-xs text-gray-400">All systems operational</span>
                </div>
              </div>
            </div>

            {/* Links - Compact rows */}
            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-3 text-xs">
              {/* Ecosystem */}
              <div>
                <h4 className="font-semibold text-gray-400 uppercase tracking-wider mb-2 text-[10px]">Ecosystem</h4>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  <a href="https://tripnovac.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">Platform</a>
                  <a href="https://tripnovac-office.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">Office</a>
                  <a href="https://tripnovac-visa.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">Visa</a>
                  <a href="https://tripnovac-ai.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">AI</a>
                  <a href="https://tripnovac-db.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">DB</a>
                  <a href="https://tripnovac-store.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">Store</a>
                  <a href="https://tripnovac-agent.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">Agent</a>
                  <a href="https://tripnovac-hub.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">Hub</a>
                </div>
              </div>
              {/* Features */}
              <div>
                <h4 className="font-semibold text-gray-400 uppercase tracking-wider mb-2 text-[10px]">Features</h4>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  <a href="/campaigns" className="text-gray-500 hover:text-white">Email</a>
                  <a href="/sms" className="text-gray-500 hover:text-white">SMS</a>
                  <a href="/automation" className="text-gray-500 hover:text-white">Automation</a>
                  <a href="/leads" className="text-gray-500 hover:text-white">Leads</a>
                  <a href="/contacts" className="text-gray-500 hover:text-white">CRM</a>
                  <a href="/analytics" className="text-gray-500 hover:text-white">Analytics</a>
                </div>
              </div>
              {/* Resources */}
              <div>
                <h4 className="font-semibold text-gray-400 uppercase tracking-wider mb-2 text-[10px]">Resources</h4>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  <a href="/api-docs" className="text-gray-500 hover:text-white">API Docs</a>
                  <a href="https://tripnovac.com/help" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">Help</a>
                  <a href="https://tripnovac.com/blog" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">Blog</a>
                  <a href="https://status.tripnovac.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">Status</a>
                </div>
              </div>
              {/* Company */}
              <div>
                <h4 className="font-semibold text-gray-400 uppercase tracking-wider mb-2 text-[10px]">Company</h4>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  <a href="https://tripnovac.com/about" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">About</a>
                  <a href="https://tripnovac.com/careers" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">Careers</a>
                  <a href="https://tripnovac.com/contact" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">Contact</a>
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">Privacy</a>
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">Terms</a>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="mt-5 pt-4 border-t border-gray-800 flex flex-col md:flex-row items-center justify-between gap-3">
              <div className="text-[11px] text-gray-600">© 2026 Tripnovac by Alpha Libra LLC. All rights reserved.</div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <a href="https://twitter.com/tripnovac" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-white"><svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg></a>
                  <a href="https://linkedin.com/company/tripnovac" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-white"><svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg></a>
                  <a href="https://github.com/tripnovac" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-white"><svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg></a>
                </div>
                <span className="text-[10px] text-gray-600 font-mono">v2.0.0</span>
              </div>
            </div>
          </div>
        </footer>
      </motion.main>
    </div>
  );
};

export default PremiumMainLayout;
